import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDaysIcon, CheckCircle2Icon, ChevronRightIcon, Clock3Icon, PackageIcon, SearchIcon, TruckIcon, XCircleIcon } from 'lucide-react';
import { OrderDetailsModal } from './modals/OrderDetailsModal';
import { ReturnRequestModal } from './modals/ReturnRequestModal';
type OrderStatus = 'Delivered' | 'Processing' | 'Shipped' | 'Cancelled';
type DateRange = 'all' | '24h' | 'week' | 'month' | 'year';
type OrderItem = {
  id: number;
  name: string;
  quantity: number;
  price: number;
  image: string;
};
type Order = {
  id: string;
  date: string;
  total: number;
  status: OrderStatus;
  items: OrderItem[];
  trackingNumber?: string;
  shippingAddress: string;
};
const productImages = ['https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop', 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop', 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&h=400&fit=crop', 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=400&h=400&fit=crop'];
const buildDate = (daysAgo: number) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString();
};
const orders: Order[] = [{
  id: 'ORD-12856',
  date: buildDate(0),
  total: 125.5,
  status: 'Processing',
  items: [{
    id: 1,
    name: 'Panadol Extra Pain Relief Tablets 24s',
    quantity: 2,
    price: 25.98,
    image: productImages[0]
  }, {
    id: 2,
    name: 'Vitamin D3 Supplement',
    quantity: 1,
    price: 24.99,
    image: productImages[1]
  }],
  trackingNumber: 'TRK123456789',
  shippingAddress: 'Al Yasmin, Riyadh'
}, {
  id: 'ORD-12855',
  date: buildDate(1),
  total: 75.25,
  status: 'Shipped',
  items: [{
    id: 3,
    name: 'Blood Pressure Monitor',
    quantity: 1,
    price: 75.25,
    image: productImages[2]
  }],
  trackingNumber: 'TRK123456788',
  shippingAddress: 'Al Malqa, Riyadh'
}, {
  id: 'ORD-12854',
  date: buildDate(4),
  total: 210,
  status: 'Delivered',
  items: [{
    id: 4,
    name: 'First Aid Kit',
    quantity: 1,
    price: 45.99,
    image: productImages[3]
  }, {
    id: 5,
    name: 'Omega-3 Fish Oil',
    quantity: 3,
    price: 54.67,
    image: productImages[1]
  }],
  trackingNumber: 'TRK123456787',
  shippingAddress: 'Al Olaya, Riyadh'
}, {
  id: 'ORD-12853',
  date: buildDate(6),
  total: 45.99,
  status: 'Delivered',
  items: [{
    id: 6,
    name: 'Digital Thermometer',
    quantity: 1,
    price: 45.99,
    image: productImages[0]
  }],
  shippingAddress: 'Al Quds, Riyadh'
}, {
  id: 'ORD-12852',
  date: buildDate(12),
  total: 88.4,
  status: 'Cancelled',
  items: [{
    id: 7,
    name: 'Children’s Multivitamin Gummies',
    quantity: 2,
    price: 44.2,
    image: productImages[1]
  }],
  shippingAddress: 'Al Nakheel, Riyadh'
}, {
  id: 'ORD-12851',
  date: buildDate(18),
  total: 152.75,
  status: 'Delivered',
  items: [{
    id: 8,
    name: 'Cetaphil Gentle Skin Cleanser',
    quantity: 1,
    price: 65,
    image: productImages[2]
  }, {
    id: 9,
    name: 'Sunscreen SPF 50',
    quantity: 1,
    price: 87.75,
    image: productImages[3]
  }],
  shippingAddress: 'Al Yasmin, Riyadh'
}, {
  id: 'ORD-12850',
  date: buildDate(29),
  total: 96.5,
  status: 'Delivered',
  items: [{
    id: 10,
    name: 'Probiotic Capsules',
    quantity: 1,
    price: 96.5,
    image: productImages[1]
  }],
  shippingAddress: 'Al Olaya, Riyadh'
}, {
  id: 'ORD-12849',
  date: buildDate(54),
  total: 185,
  status: 'Delivered',
  items: [{
    id: 11,
    name: 'Baby Formula Stage 2',
    quantity: 2,
    price: 92.5,
    image: productImages[3]
  }],
  shippingAddress: 'Al Malqa, Riyadh'
}, {
  id: 'ORD-12848',
  date: buildDate(118),
  total: 64.9,
  status: 'Delivered',
  items: [{
    id: 12,
    name: 'Allergy Relief Tablets',
    quantity: 1,
    price: 64.9,
    image: productImages[0]
  }],
  shippingAddress: 'Al Yasmin, Riyadh'
}, {
  id: 'ORD-12847',
  date: buildDate(220),
  total: 142.3,
  status: 'Delivered',
  items: [{
    id: 13,
    name: 'Magnesium Citrate',
    quantity: 2,
    price: 71.15,
    image: productImages[1]
  }],
  shippingAddress: 'Al Nakheel, Riyadh'
}];
const dateFilters: Array<{
  id: DateRange;
  label: string;
}> = [{
  id: 'all',
  label: 'All orders'
}, {
  id: '24h',
  label: 'Last 24 hours'
}, {
  id: 'week',
  label: 'Last week'
}, {
  id: 'month',
  label: 'Last month'
}, {
  id: 'year',
  label: 'This year'
}];
const statusConfig: Record<OrderStatus, {
  icon: typeof CheckCircle2Icon;
  className: string;
}> = {
  Delivered: {
    icon: CheckCircle2Icon,
    className: 'bg-emerald-50 text-emerald-700'
  },
  Processing: {
    icon: Clock3Icon,
    className: 'bg-amber-50 text-amber-800'
  },
  Shipped: {
    icon: TruckIcon,
    className: 'bg-sky-50 text-sky-700'
  },
  Cancelled: {
    icon: XCircleIcon,
    className: 'bg-rose-50 text-rose-700'
  }
};
export function OrdersSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [visibleCount, setVisibleCount] = useState(5);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const filteredOrders = useMemo(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const rangeStart: Record<DateRange, Date | null> = {
      all: null,
      '24h': new Date(now.getTime() - 24 * 60 * 60 * 1000),
      week: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      month: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      year: startOfYear
    };
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return orders.filter(order => {
      const inDateRange = !rangeStart[dateRange] || new Date(order.date) >= rangeStart[dateRange]!;
      const matchesSearch = !normalizedSearch || order.id.toLowerCase().includes(normalizedSearch) || order.items.some(item => item.name.toLowerCase().includes(normalizedSearch));
      return inDateRange && matchesSearch;
    });
  }, [dateRange, searchQuery]);
  useEffect(() => {
    setVisibleCount(5);
  }, [dateRange, searchQuery]);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || visibleCount >= filteredOrders.length) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisibleCount(current => Math.min(current + 4, filteredOrders.length));
    }, {
      rootMargin: '180px'
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [filteredOrders.length, visibleCount]);
  const visibleOrders = filteredOrders.slice(0, visibleCount);
  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };
  const handleCancelOrder = (order: Order) => {
    setOrderDetailsOpen(false);
    setSelectedOrder(order);
    window.setTimeout(() => setReturnModalOpen(true), 140);
  };
  return <div className="mx-auto max-w-2xl space-y-5 pb-5" data-id="element-704" data-mp="svfmgc5hfiui">
    <label className="relative block" data-id="element-705" data-mp="sx0env4bqpp7"><SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" data-id="element-706" data-mp="2s89wgisujkrl" /><input type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search order or product" className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-gray-400 focus:border-[#16726c] focus:ring-2 focus:ring-[#16726c]/20" data-id="element-707" data-mp="2n8f7iksmt596" /></label>

    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Order date filter" data-id="element-708" data-mp="39hc6ihzyie64">
      {dateFilters.map(filter => <button key={filter.id} type="button" role="tab" aria-selected={dateRange === filter.id} onClick={() => setDateRange(filter.id)} className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold transition-[background-color,color,border-color] duration-150 ${dateRange === filter.id ? 'bg-[#16726c] text-white' : 'border border-gray-200 bg-white text-gray-600'}`} data-id="element-709" data-mp="2grn3jz3os41v">{filter.label}</button>)}
    </div>

    {visibleOrders.length > 0 ? <div className="space-y-2.5" data-id="element-710" data-mp="5ysmg2rfmeme">
      {visibleOrders.map(order => {
        const status = statusConfig[order.status];
        const StatusIcon = status.icon;
        const primaryItem = order.items[0];
        return <article key={order.id} className="rounded-2xl border border-gray-200 bg-white p-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)]" data-id="element-711" data-mp="n4nmejhwu70h">
          <button type="button" onClick={() => handleViewOrder(order)} className="flex w-full items-center gap-3 text-left" aria-label={`View ${order.id}`} data-id="element-712" data-mp="w3r6nqstcsgk">
            <img src={primaryItem.image} alt="" className="h-14 w-14 shrink-0 rounded-xl border border-gray-100 bg-gray-50 object-cover" data-id="element-713" data-mp="1aqkc84cj6jvk" />
            <span className="min-w-0 flex-1" data-id="element-714" data-mp="1jstlyrzz697w"><span className="flex items-center justify-between gap-2" data-id="element-715" data-mp="1js0mmc3cilzs"><span className="text-sm font-bold text-gray-900" data-id="element-716" data-mp="1etz5vxte6yrh">{order.id}</span><span className="text-sm font-bold text-[#16726c]" data-id="element-717" data-mp="1euif8q4z8etw">SAR {order.total.toFixed(2)}</span></span><span className="mt-1 flex items-center gap-1.5 text-xs text-gray-500" data-id="element-718" data-mp="3lkylopnpuat6"><CalendarDaysIcon className="h-3.5 w-3.5" data-id="element-719" data-mp="3gm57sw4iohsh" />{new Date(order.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })} · {order.items.length} {order.items.length === 1 ? 'item' : 'items'}</span><span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${status.className}`} data-id="element-720" data-mp="212nuc9s2hege"><StatusIcon className="h-3.5 w-3.5" data-id="element-721" data-mp="1na7shcawjmh2" />{order.status}</span></span>
            <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" data-id="element-722" data-mp="1rbt7b36zyatw" />
          </button>
        </article>;
      })}
      {visibleCount < filteredOrders.length && <div ref={loadMoreRef} className="flex items-center justify-center py-4 text-xs font-semibold text-gray-400" data-id="element-723" data-mp="1df644iogaofj"><span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#16726c]" data-id="element-724" data-mp="14c53gxictpj9" />Loading more orders</div>}
    </div> : <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center" data-id="element-725" data-mp="18ei5pbxw75ba"><PackageIcon className="mx-auto h-8 w-8 text-gray-300" data-id="element-726" data-mp="2yr7n4ekq2de9" /><h3 className="mt-3 text-base font-bold text-gray-900" data-id="element-727" data-mp="2yoblaf5r74kj">No orders found</h3><p className="mt-1 text-sm text-gray-500" data-id="element-728" data-mp="32qykj14r7rrr">Try another period or search term.</p></div>}

    <OrderDetailsModal isOpen={orderDetailsOpen} onClose={() => setOrderDetailsOpen(false)} order={selectedOrder} onCancelOrder={handleCancelOrder} data-id="element-729" data-mp="khdhf10gua6i" />
    <ReturnRequestModal isOpen={returnModalOpen} onClose={() => setReturnModalOpen(false)} order={selectedOrder} onSubmit={() => setReturnModalOpen(false)} data-id="element-730" data-mp="al217zxr906s" />
  </div>;
}